'use client';

import { useEffect, useMemo, useState } from 'react';
import { getMyTeamMembers } from '@/app/apis';
import { useFetch } from './use-fetch';

export type TeamCreatorOption = {
  key: string;
  label: string;
  searchLabel?: string;
};

type TeamCreatorMeta = {
  id: string;
  nickname?: string;
  avatar?: string;
};

type TeamCreatorMember = {
  memberUserId: string;
  nickname?: string;
  account?: string;
  avatar?: string;
};

export const useTeamCreatorOptions = (teamSpaceId?: string) => {
  const [teamMembers, setTeamMembers] = useState<TeamCreatorMember[]>([]);

  const { run: fetchTeamMembers } = useFetch(getMyTeamMembers, {
    manual: true,
    onSuccess: ({ data, error }) => {
      if (error) {
        return;
      }

      const members = data?.members ?? [];
      const normalizedMembers: TeamCreatorMember[] = [];

      members.forEach((member) => {
        const memberUserId = member.member_user_id;
        if (!memberUserId) {
          return;
        }

        const normalized = member as typeof member & { avatar?: string };
        normalizedMembers.push({
          memberUserId,
          nickname: member.nickname,
          account: member.account,
          avatar: normalized.avatar,
        });
      });

      setTeamMembers(normalizedMembers);
    },
  });

  useEffect(() => {
    if (!teamSpaceId) {
      setTeamMembers([]);
      return;
    }

    fetchTeamMembers();
  }, [fetchTeamMembers, teamSpaceId]);

  const creatorFilterOptions = useMemo(() => {
    const optionsMap = new Map<string, TeamCreatorOption>();

    teamMembers.forEach((member) => {
      optionsMap.set(member.memberUserId, {
        key: member.memberUserId,
        label: member.nickname || member.account || member.memberUserId,
        searchLabel: member.nickname || '',
      });
    });

    return Array.from(optionsMap.values()).sort((left, right) => left.label.localeCompare(right.label));
  }, [teamMembers]);

  const creatorMetaMap = useMemo(() => {
    const map = new Map<string, TeamCreatorMeta>();

    teamMembers.forEach((member) => {
      map.set(member.memberUserId, {
        id: member.memberUserId,
        nickname: member.nickname || member.account || member.memberUserId,
        avatar: member.avatar,
      });
    });

    return map;
  }, [teamMembers]);

  return {
    creatorFilterOptions,
    creatorMetaMap,
  };
};
